import { useCallback, useEffect, useState } from "react";
import { dateAndTimeToHHMM, resultDurationMinutes } from "@/pages/TaskResults";
import { formatDateKey, minutesToTimeString } from "@/lib/time";
import type { TaskEditSheetTask } from "@/components/tasks/sheets/TaskEditSheet/TaskEditSheet";

export type TaskEditorSchedule = {
  date: string | null;
  startTime: string | null;
  durationMin: number | null;
  alertEnabled: boolean;
};

export type TaskEditorStatus = {
  value: TaskEditSheetTask["status"];
  date: string | null;
  startedAt: Date | null;
  completedAt: Date | null;
  durationMin: number | null;
};

export type TaskEditorModel = {
  schedule: TaskEditorSchedule;
  status: TaskEditorStatus;
};

function asDate(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function createTaskEditorModel(task: TaskEditSheetTask): TaskEditorModel {
  const startedAt = asDate(task.startedAt);
  const completedAt = asDate(task.completedAt);
  return {
    schedule: {
      date: task.date ?? null,
      startTime: task.startMinutes == null ? null : minutesToTimeString(task.startMinutes),
      durationMin: task.durationMin ?? null,
      alertEnabled: task.alertEnabled ?? false,
    },
    status: {
      value: task.status,
      date: startedAt
        ? formatDateKey(startedAt)
        : completedAt
          ? formatDateKey(completedAt)
          : null,
      startedAt,
      completedAt,
      durationMin: resultDurationMinutes(startedAt, completedAt)
        ? Number(resultDurationMinutes(startedAt, completedAt))
        : null,
    },
  };
}

export function useTaskEditorModel(task: TaskEditSheetTask) {
  const [model, setModel] = useState(() => createTaskEditorModel(task));

  useEffect(() => {
    setModel(createTaskEditorModel(task));
  }, [task.id]);

  const updateSchedule = useCallback((patch: Partial<TaskEditorSchedule>) => {
    setModel(current => ({ ...current, schedule: { ...current.schedule, ...patch } }));
  }, []);

  const updateStatus = useCallback((patch: Partial<TaskEditorStatus>) => {
    setModel(current => ({ ...current, status: { ...current.status, ...patch } }));
  }, []);

  return { model, updateSchedule, updateStatus, setModel };
}

export function formatEditorStatusTime(value: Date | null) {
  return dateAndTimeToHHMM(value);
}
